import { useRouter } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";
import {
  Badge,
  Button,
  Card,
  Divider,
  Input,
  Screen,
  Text,
  ThemeToggle,
  useTheme,
  useThemedStyles,
} from "~/components/ui";
import { AccountData } from "~/components/AccountData";
import { TwoFactorCard } from "~/components/TwoFactorCard";
import { AppVersionPanel } from "~/lib/app-updates";
import { useAuth } from "~/lib/auth";
import { radius, spacing, type ThemeColors } from "~/styles/tokens";
import { confirmAsync } from "~/utils/confirm";
import { notify } from "~/utils/notify";

export default function ProfileScreen() {
  const router = useRouter();
  const { user, hasPro, signOut, updateProfile } = useAuth();
  const { colors } = useTheme();
  const styles = useStyles();
  const isAnonymous = !user;

  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(user?.name ?? "");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const handleLogout = async () => {
    const confirmed = await confirmAsync({
      title: "Sign out",
      message: "You'll be signed back in as a guest automatically.",
      confirmLabel: "Sign out",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await signOut();
    } catch (error) {
      notify(
        "Error",
        error instanceof Error
          ? error.message
          : "Could not sign out. Please try again.",
      );
    }
  };

  const handleSaveName = async () => {
    if (!displayName.trim()) {
      setNameError("Name cannot be empty.");
      return;
    }
    setNameLoading(true);
    setNameError(null);
    const result = await updateProfile(displayName.trim());
    setNameLoading(false);
    if (result.error) {
      setNameError(result.error);
      return;
    }
    setEditingName(false);
  };

  return (
    <Screen scroll>
      <View style={styles.header}>
        <View style={styles.avatar}>
          <Text style={styles.avatarEmoji}>{isAnonymous ? "🥐" : "👩‍🍳"}</Text>
        </View>
        <Text variant="heading" align="center">
          {isAnonymous ? "Guest baker" : user?.name || user?.email || "Baker"}
        </Text>
        {isAnonymous ? (
          <Badge label="Anonymous" style={styles.headerBadge} />
        ) : (
          <Badge
            label={user?.emailVerified ? "Verified member" : "Member"}
            variant={user?.emailVerified ? "success" : "default"}
            style={styles.headerBadge}
          />
        )}
        {!isAnonymous && user?.email ? (
          <Text variant="caption">{user.email}</Text>
        ) : null}
      </View>

      {isAnonymous ? (
        <Card elevation="flat" style={styles.card}>
          <Text variant="title">Keep this baker across devices</Text>
          <Text variant="body" color={colors.heading} style={styles.cardBody}>
            Save an account, then go Pro, to sync your starter and timers across
            devices and get notified when the app is closed.
          </Text>
          <Button
            label="Save account"
            onPress={() => router.push("/auth")}
            variant="primary"
            style={styles.cardAction}
          />
        </Card>
      ) : (
        <Card elevation="raised" style={styles.card}>
          <Text variant="label" style={styles.cardLabel}>
            Your account
          </Text>
          {editingName ? (
            <>
              <Input
                label="Display name"
                value={displayName}
                onChangeText={setDisplayName}
                autoCapitalize="words"
                autoComplete="username"
              />
              {nameError ? (
                <Text
                  variant="caption"
                  color={colors.danger}
                  style={styles.nameError}
                >
                  {nameError}
                </Text>
              ) : null}
              <View style={styles.nameRow}>
                <Button
                  label="Save"
                  onPress={() => void handleSaveName()}
                  loading={nameLoading}
                  variant="primary"
                  size="sm"
                  style={styles.nameRowBtn}
                />
                <Button
                  label="Cancel"
                  onPress={() => {
                    setEditingName(false);
                    setDisplayName(user?.name ?? "");
                    setNameError(null);
                  }}
                  variant="ghost"
                  size="sm"
                  style={styles.nameRowBtn}
                />
              </View>
            </>
          ) : (
            <Button
              label="Edit display name"
              onPress={() => {
                setDisplayName(user?.name ?? "");
                setEditingName(true);
              }}
              variant="secondary"
              fullWidth
            />
          )}
        </Card>
      )}

      {!isAnonymous ? (
        <Card elevation="raised" style={styles.card}>
          <View style={styles.appearanceRow}>
            <View style={{ flex: 1 }}>
              <Text variant="label" style={styles.cardLabel}>
                DoughBro Pro
              </Text>
              <Text variant="body" color={colors.heading}>
                {hasPro
                  ? "Your starter and timers sync, with notifications when the app is closed."
                  : "Sync across devices and get notified when the app is closed."}
              </Text>
            </View>
            <Badge
              label={hasPro ? "Active" : "Free"}
              variant={hasPro ? "success" : "default"}
            />
          </View>
          <Button
            label={hasPro ? "Manage subscription" : "See DoughBro Pro"}
            onPress={() => router.push("/subscription" as never)}
            variant="secondary"
            fullWidth
            style={styles.cardAction}
          />
        </Card>
      ) : null}

      <Card elevation="raised" style={styles.card}>
        <Text variant="label" style={styles.cardLabel}>
          Appearance
        </Text>
        <View style={styles.appearanceRow}>
          <Text variant="body">Light, dark, or match your device</Text>
          <ThemeToggle />
        </View>
      </Card>

      <Card elevation="raised" style={styles.card}>
        <Text variant="label" style={styles.cardLabel}>
          About DoughBro
        </Text>
        <Text variant="body" color={colors.heading}>
          Your sourdough sidekick 🍞
        </Text>
        <AppVersionPanel
          colors={{
            text: colors.foreground,
            muted: colors.muted,
            border: colors.border,
            accent: colors.primary,
            onAccent: colors.primaryForeground,
            danger: colors.danger,
          }}
        />
      </Card>

      {!isAnonymous ? (
        <Button
          label="Support"
          onPress={() => router.push("/support" as never)}
          variant="secondary"
          fullWidth
        />
      ) : null}

      {!isAnonymous && user?.email ? <AccountData email={user.email} /> : null}

      {!isAnonymous ? <TwoFactorCard /> : null}

      {!isAnonymous ? (
        <>
          <Divider style={styles.divider} />
          <Button
            label="Sign out"
            onPress={handleLogout}
            variant="secondary"
            fullWidth
            style={styles.signOut}
          />
          <View style={{ height: spacing.xl }} />
        </>
      ) : null}
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    header: {
      alignItems: "center",
      paddingTop: spacing.xl,
      paddingBottom: spacing.lg,
      gap: spacing.sm,
    },
    avatar: {
      width: 84,
      height: 84,
      borderRadius: radius.full,
      backgroundColor: c.background,
      borderWidth: 1,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: spacing.xs,
    },
    appearanceRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.sm,
    },
    avatarEmoji: {
      fontSize: 40,
    },
    headerBadge: {
      alignSelf: "center",
    },
    card: {
      marginTop: spacing.md,
      gap: spacing.xs,
    },
    cardLabel: {
      marginBottom: spacing.xs,
    },
    cardBody: {
      marginTop: spacing.xs,
    },
    cardAction: {
      marginTop: spacing.md,
      alignSelf: "flex-start",
    },
    nameRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    nameRowBtn: {
      flex: 1,
    },
    nameError: {
      marginTop: spacing.xs,
    },
    version: {
      marginTop: spacing.xs,
    },
    divider: {
      marginTop: spacing.lg,
      marginBottom: spacing.md,
    },
    signOut: {
      marginBottom: spacing.sm,
    },
  });
